import {MouseEventHandler, useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {Navigate, useNavigate} from 'react-router-dom';

import {Box, Container, Grid, Grow, Typography} from '@mui/material';

import cover_image_big from '../../../assets/company_survey_page/company-survey-cover-image-big.png';
import cover_image_small from '../../../assets/company_survey_page/company-survey-cover-image-small.png';
import {EmpionButton} from '../../../components/EmpionButton/EmpionButton';
import {EmpionPaper} from '../../../components/EmpionPaper/EmpionPaper';
import {LoadingContainer} from '../../../components/Loading/LoadingContainer';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../contexts/SnackBarContext/SnackBarContext';
import {PreQuestionnaireQuestions} from '../../../features/company/culture/surveys/components/survey-questionnaire/pre-questionnaire-questions/PreQuestionnaireQuestions';
import {CompanySurveyQuestionnairePayload} from '../../../features/company/culture/surveys/types/SurveyQuestionnaireTypes';
import {useSurveyStatus} from '../../../hooks/useSurveyStatus';
import {greenColors} from '../../../layouts/theme/designSystem';
import {AppConfig} from '../../../lib/AppConfig';

interface formDataType {
  teamLeadershipResponsibility: string | undefined;
  disciplinaryResponsibility: string | undefined;
}

export const CompanyCultureSurveyWelcomePage = () => {
  const {i18n, t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const surveyCode = new URLSearchParams(window.location.search).get('code');
  const navigate = useNavigate();

  const lang = useContext(LanguageContext);

  const [formData, setFormData] = useState<formDataType>({
    teamLeadershipResponsibility: undefined,
    disciplinaryResponsibility: undefined,
  });
  const [surveyStarted, setSurveyStarted] = useState<boolean>(false);

  const {status, surveyConfiguration, isLoading, error} = useSurveyStatus(surveyCode);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initLanguage = urlParams.get('initLang');

    if (initLanguage) {
      i18n.changeLanguage(initLanguage);
    }
  }, [i18n, surveyCode]);

  const startQuestionnaire = async (e: any) => {
    e.preventDefault();

    const companySurveyQuestionnaire: CompanySurveyQuestionnairePayload = {
      companySurveyQuestionnaire: {
        surveyCode: surveyCode || '',
        language: lang,
        identifyingQuestions: {
          teamLeadershipResponsibility: formData?.teamLeadershipResponsibility === 'true',
          disciplinaryResponsibility: formData?.disciplinaryResponsibility === 'true',
        },
      },
    };

    navigate(`${AppConfig.COMPANY_SURVEY_QUESTIONNAIRE_PATH}?code=${surveyCode}`, {
      state: {...companySurveyQuestionnaire, surveyConfiguration},
    });
  };

  function switchLanguage(): MouseEventHandler<HTMLButtonElement> | undefined {
    return (e: any) => {
      e.preventDefault();
      const language = i18n.resolvedLanguage as 'en' | 'de';

      if (language === 'en') {
        i18n.changeLanguage('de');
      } else {
        i18n.changeLanguage('en');
      }
    };
  }

  if (isLoading) {
    return <LoadingContainer minHeight='400px' />;
  }

  if (error) {
    dispatchMessage(t('companySurveys.public.home.error'), 'error');
    return <Navigate to={'/'} />;
  }

  if (surveyStarted) {
    return (
      <Grid container justifyContent='center' alignItems='center'>
        <Grid item xs={12}>
          <PreQuestionnaireQuestions
            formData={formData}
            setFormData={setFormData}
            startQuestionnaire={startQuestionnaire}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container columns={{xs: 4, sm: 6, md: 12, lg: 12}} spacing={{xs: 2, md: 3}} alignItems={'center'}>
        {status === 'active' && (
          <>
            <Grid item xs={6}>
              <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
                <Container>
                  <Box
                    marginBottom={5}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: {xs: 'center', sm: 'center', md: 'flex-start', lg: 'flex-start'},
                      textAlign: {xs: 'center', sm: 'center', md: 'left', lg: 'left'},
                    }}
                  >
                    <Typography
                      variant='h1'
                      gutterBottom
                      fontWeight={'400'}
                      textTransform={'uppercase'}
                      sx={{
                        fontSize: {xs: '3rem', sm: '3rem', md: '3rem', lg: '4.5rem'},
                        lineHeight: {xs: '3.5rem', sm: '3rem', md: '3rem', lg: '4.5rem'},
                      }}
                    >
                      {t('companySurveys.public.home.title')}
                    </Typography>

                    <Typography variant='h4' mb={1}>
                      {t('companySurveys.public.home.subtitle')}
                    </Typography>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: {xs: 'block', sm: 'block', md: 'none', lg: 'none'},
                      }}
                    >
                      <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
                        <Box display={'flex'} justifyContent={'flex-end'}>
                          <img
                            src={cover_image_small}
                            alt='cover_image_small'
                            style={{width: '100%', height: 'auto'}}
                          />
                        </Box>
                      </Grow>
                    </Grid>

                    <Typography variant='h5'>{t('companySurveys.public.home.description')}</Typography>
                  </Box>
                  <Box
                    display={'flex'}
                    gap={2}
                    sx={{
                      justifyContent: {xs: 'center', sm: 'center', md: 'flex-start', lg: 'flex-start'},
                    }}
                  >
                    <EmpionButton size={'large'} variant='cta' onClick={() => setSurveyStarted(true)}>
                      {t('companySurveys.public.home.startQuestionnaire')}
                    </EmpionButton>
                    <EmpionButton size={'large'} variant='secondary' onClick={switchLanguage()}>
                      {t('companySurveys.public.home.changeLanguage')}
                    </EmpionButton>
                  </Box>
                </Container>
              </Grow>
            </Grid>
            <Grid
              item
              xs={6}
              padding={5}
              sx={{
                display: {xs: 'none', sm: 'none', md: 'block', lg: 'block'},
              }}
            >
              <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
                <Container>
                  <Box display={'flex'} justifyContent={'flex-end'}>
                    <img src={cover_image_big} alt='cover_image_big' style={{width: '100%', height: 'auto'}} />
                  </Box>
                </Container>
              </Grow>
            </Grid>
          </>
        )}

        <Grid
          container
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {status === 'not-started' && (
            <EmpionPaper
              sx={{
                backgroundColor: '#F5F5F5',
                padding: '3rem',
                marginTop: '10rem',
              }}
            >
              <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
                <Typography
                  variant='h1'
                  gutterBottom
                  align='center'
                  sx={{
                    color: greenColors[700],
                  }}
                >
                  {t('companySurveys.public.home.surveyNotStarted')}
                </Typography>
              </Grow>
            </EmpionPaper>
          )}
          {status === 'ended' && (
            <EmpionPaper
              sx={{
                backgroundColor: '#F5F5F5',
                padding: '3rem',
                marginTop: '10rem',
              }}
            >
              <Grow in style={{transformOrigin: '0 0 0'}} timeout={1200}>
                <Typography
                  variant='h1'
                  gutterBottom
                  align='center'
                  sx={{
                    color: greenColors[700],
                  }}
                >
                  {t('companySurveys.public.home.surveyClosed')}
                </Typography>
              </Grow>
            </EmpionPaper>
          )}
        </Grid>
      </Grid>
    </>
  );
};
